import { useState } from 'react';
import amperaLogo from '../../assests/ampera_logo.png';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { isValidIndonesianPhone } from '../../lib/utils';
import LanguageSwitcher from '../../components/layout/LanguageSwitcher';
import { Button, Field } from '../../components/ui';

export default function ForgotPassword() {
  const { t } = useTranslation();
  const { sendOtp, verifyOtp, resetPasswordWithOtp, loading: authLoading } = useAuth();

  // Form state
  const [step, setStep] = useState('phone'); // 'phone', 'otp', 'password', 'done'
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Step 1: Send OTP
  const handleSendOtp = async (e) => {
    e.preventDefault();
    setError('');

    if (!isValidIndonesianPhone(phone)) {
      setError(t('auth.invalidPhone'));
      return;
    }

    setLoading(true);
    const { error: otpError } = await sendOtp(phone, { shouldCreateUser: false });

    if (otpError) {
      setError(otpError.message || t('auth.otpFailed'));
      setLoading(false);
      return;
    }

    setStep('otp');
    setLoading(false);
  };

  // Step 2: Verify OTP
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError('');

    if (otp.length < 6) {
      setError(t('auth.otpInvalid'));
      return;
    }

    // Check the code now — Message Central codes expire in about a minute,
    // too short to also type a new password first.
    setLoading(true);
    const { error: verifyError } = await verifyOtp(phone, otp, { shouldCreateUser: false });
    setLoading(false);
    if (verifyError) {
      setError(verifyError.message || t('auth.otpInvalid'));
      return;
    }
    setStep('password');
  };

  // Step 3: Reset password
  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword.length < 6) {
      setError(t('auth.passwordTooShort'));
      return;
    }

    if (newPassword !== confirmPassword) {
      setError(t('auth.passwordMismatch'));
      return;
    }

    setLoading(true);
    const { error: resetError } = await resetPasswordWithOtp(phone, otp, newPassword);

    if (resetError) {
      setError(resetError.message || t('auth.resetFailed'));
      setLoading(false);
      return;
    }

    setStep('done');
    setLoading(false);
  };

  // Success state
  if (step === 'done') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-white flex items-center justify-center py-12 px-4">
        <div className="fixed top-4 right-4 z-50">
          <LanguageSwitcher />
        </div>
        <div className="max-w-md w-full">
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {t('auth.passwordResetSuccess')}
            </h2>
            <p className="text-gray-600 mb-6">
              {t('auth.passwordResetDone')}
            </p>
            <Link to="/login" className="btn-primary inline-block">
              {t('auth.goToLogin')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-white flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      {/* Language Switcher */}
      <div className="fixed top-4 right-4 z-50">
        <LanguageSwitcher />
      </div>

      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/">
            <img
              src={amperaLogo}
              alt="Ampera"
              className="h-16 mx-auto"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          </Link>
          <h1 className="mt-4 text-3xl font-display font-bold text-gray-900">
            {t('auth.forgotPasswordTitle')}
          </h1>
          <p className="mt-2 text-gray-600">
            {t('auth.forgotPasswordSubtitle')}
          </p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          {/* Step Indicator */}
          <div className="flex items-center justify-center mb-6 gap-2">
            {['phone', 'otp', 'password'].map((s, i) => (
              <div key={s} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  ['phone', 'otp', 'password'].indexOf(step) >= i
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-200 text-gray-500'
                }`}>
                  {i + 1}
                </div>
                {i < 2 && (
                  <div className={`w-8 h-0.5 ${
                    ['phone', 'otp', 'password'].indexOf(step) > i
                      ? 'bg-primary-600'
                      : 'bg-gray-200'
                  }`} />
                )}
              </div>
            ))}
          </div>

          {/* Step 1: Phone */}
          {step === 'phone' && (
            <form onSubmit={handleSendOtp} className="space-y-4">
              <Field id="phone" label={t('auth.phoneNumber')}>
                <input
                  id="phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="08xxxxxxxxxx"
                  className="input-field"
                  required
                />
              </Field>

              <Button
                type="submit"
                disabled={loading || authLoading}
                className="w-full disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? t('auth.sendingOtp') : t('auth.sendOtp')}
              </Button>
            </form>
          )}

          {/* Step 2: OTP */}
          {step === 'otp' && (
            <form onSubmit={handleVerifyOtp} className="space-y-4">
              <p className="text-sm text-gray-600 mb-4">
                {t('auth.otpSentTo', { phone })}
              </p>

              <Field id="otp" label={t('auth.otpCode')}>
                <input
                  id="otp"
                  type="text"
                  inputMode="numeric"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  placeholder="123456"
                  className="input-field text-center text-2xl tracking-widest"
                  maxLength={6}
                  required
                />
              </Field>

              <Button
                type="submit"
                disabled={loading || otp.length < 6}
                className="w-full disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? t('auth.verifying') : t('auth.verify')}
              </Button>

              <button
                type="button"
                onClick={() => {
                  setStep('phone');
                  setOtp('');
                }}
                className="w-full text-primary-600 text-sm hover:underline"
              >
                {t('auth.backToLogin')}
              </button>
            </form>
          )}

          {/* Step 3: New Password */}
          {step === 'password' && (
            <form onSubmit={handleResetPassword} className="space-y-4">
              <Field id="newPassword" label={t('auth.newPassword')} hint={t('auth.passwordHint')}>
                <input
                  id="newPassword"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="••••••••"
                  className="input-field"
                  required
                  minLength={6}
                />
              </Field>

              <Field id="confirmPassword" label={t('auth.confirmPassword')}>
                <input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  className="input-field"
                  required
                />
              </Field>

              <Button
                type="submit"
                disabled={loading || authLoading}
                className="w-full disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? t('auth.resettingPassword') : t('auth.resetPassword')}
              </Button>
            </form>
          )}

          {/* Back to login */}
          <div className="mt-6 text-center">
            <Link to="/login" className="text-sm text-primary-600 hover:underline">
              {t('auth.backToLogin')}
            </Link>
          </div>
        </div>

        {/* Back to home */}
        <div className="mt-6 text-center">
          <Link to="/" className="text-sm text-gray-600 hover:text-gray-900">
            &larr; {t('common.backToHome')}
          </Link>
        </div>
      </div>
    </div>
  );
}
